import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert } from "@/components/ui/alert";
import { Table } from "@/components/ui/table";
import { Spinner } from "@/components/ui/spinner";
import { Trash2, Search, Plus, AlertCircle, Trash, ChevronUp, ChevronDown, ArrowUpDown, Ban, Flag } from "lucide-react";

import { WebComponents } from "@/interfaces";
import { useAccountSettings, useSocialCoachSession, useComplianceChecker } from "@/utils";
import { Tooltip } from "@/components/ui/tooltip";
import { Keyphrase, KeyphrasePolicy, KeyphraseServices } from "@/services/bedrock";
import { toast } from "sonner";

interface KeywordSetEditorProps {
  open: boolean;
  onClose: () => void;
  policy: KeyphrasePolicy;
  onUpdate: () => void;
}

type Props = KeywordSetEditorProps;
type SortColumn = "content" | "updatedDate" | "postKeyphrasesCount";
type SortDirection = "asc" | "desc";

export const KeywordSetEditor: React.FC<Props> = ({ open, onClose, policy, onUpdate }) => {
  const { coachId } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();
  const { invalidateKeywords } = useComplianceChecker();
  const withWritePermission = hasWritePermission(WebComponents.COMPLIANCE);

  const [fetchedKeywords, setFetchedKeywords] = useState<Keyphrase[]>([]);
  const [phrases, setPhrases] = useState<string[]>([]);
  const [otherPhrases, setOtherPhrases] = useState<string[]>([]);
  const [pasteText, setPasteText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // Client-side sorting states
  const [sortColumn, setSortColumn] = useState<SortColumn>("postKeyphrasesCount");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Secondary modal states to view added, removed, or moved phrases list
  const [changesModalOpen, setChangesModalOpen] = useState(false);
  const [changesModalType, setChangesModalType] = useState<"added" | "removed" | "moved" | null>(null);

  // Load and synchronize keywords
  useEffect(() => {
    if (!open) return;

    const loadData = async () => {
      setIsFetchingData(true);
      setErrors([]);
      try {
        const allKeyphrases = await KeyphraseServices.getKeyphrases({
          coachId,
          policy: ["FLAG", "BLOCK"],
          extraFields: ["post_keyphrases_count"],
        });

        setFetchedKeywords(allKeyphrases);

        const currentKeywords = allKeyphrases.filter(k => k.policy === policy);
        setPhrases(currentKeywords.map(k => k.content));

        const other = allKeyphrases.filter(k => k.policy !== policy);
        setOtherPhrases(other.map(k => k.content));
      } catch (err) {
        console.error(err);
        setErrors(["Failed to load phrases. Please try again."]);
      } finally {
        setIsFetchingData(false);
      }
    };

    loadData();
    setPasteText("");
    setSearchQuery("");
    setChangesModalOpen(false);
    setChangesModalType(null);
    setSortColumn("postKeyphrasesCount");
    setSortDirection("desc");
  }, [open, coachId, policy]);

  const label = useMemo(() => {
    switch (policy) {
      case "FLAG":
        return "Flagged Phrases";
      case "BLOCK":
        return "Blocked Phrases";
      default:
        return "Phrases";
    }
  }, [policy]);

  const initialKeywords = useMemo(() => {
    return fetchedKeywords.filter(k => k.policy === policy);
  }, [fetchedKeywords, policy]);

  const initialPhrasesLower = useMemo(() => {
    return initialKeywords.map(k => k.content.toLowerCase());
  }, [initialKeywords]);

  const addedList = useMemo(() => {
    return phrases.filter(p => !initialPhrasesLower.includes(p.toLowerCase()));
  }, [phrases, initialPhrasesLower]);

  const currentPhrasesLower = useMemo(() => phrases.map(p => p.toLowerCase()), [phrases]);
  const otherPhrasesLower = useMemo(() => otherPhrases.map(p => p.toLowerCase()), [otherPhrases]);

  const movedList = useMemo(() => {
    return initialKeywords.filter(k => otherPhrasesLower.includes(k.content.toLowerCase())).map(k => k.content);
  }, [initialKeywords, otherPhrasesLower]);

  const removedList = useMemo(() => {
    return initialKeywords
      .filter(
        k =>
          !currentPhrasesLower.includes(k.content.toLowerCase()) && !otherPhrasesLower.includes(k.content.toLowerCase())
      )
      .map(k => k.content);
  }, [initialKeywords, currentPhrasesLower, otherPhrasesLower]);

  const addedCount = addedList.length;
  const removedCount = removedList.length;
  const movedCount = movedList.length;

  const changesModalPhrases = useMemo(() => {
    if (changesModalType === "added") return addedList;
    if (changesModalType === "removed") return removedList;
    return movedList;
  }, [changesModalType, addedList, removedList, movedList]);

  const handleAddPhrases = () => {
    const parsed = pasteText
      .split(/[,\r\n\t]+/)
      .map(p => p.trim())
      .filter(p => p.length > 0);

    if (parsed.length === 0) return;

    const unavailableLower = otherPhrases.map(p => p.toLowerCase());
    const existingLower = phrases.map(p => p.toLowerCase());

    const dupesInOther: string[] = [];
    const added: string[] = [];

    parsed.forEach(phrase => {
      const lower = phrase.toLowerCase();
      if (unavailableLower.includes(lower)) {
        if (!dupesInOther.includes(phrase)) {
          dupesInOther.push(phrase);
        }
      } else if (!existingLower.includes(lower)) {
        if (!added.includes(phrase)) {
          added.push(phrase);
        }
      }
    });

    if (dupesInOther.length > 0) {
      if (dupesInOther.length === 1) {
        setErrors([`The phrase "${dupesInOther[0]}" is already configured in the other keyword set.`]);
      } else {
        setErrors([`These phrases are already configured in the other keyword set: ${dupesInOther.join(", ")}`]);
      }
    } else {
      setErrors([]);
    }

    if (added.length > 0) {
      setPhrases(prev => [...prev, ...added]);
      setPasteText("");
      setSortColumn("updatedDate");
      setSortDirection("asc");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAddPhrases();
    }
  };

  const handleRemovePhrase = (phraseToRemove: string) => {
    if (!withWritePermission) return;
    setPhrases(prev => prev.filter(p => p.toLowerCase() !== phraseToRemove.toLowerCase()));
  };

  const handleRemoveAll = () => {
    if (!withWritePermission) return;
    if (window.confirm(`Are you sure you want to remove all phrases from this ${label} list?`)) {
      setPhrases([]);
      setErrors([]);
    }
  };

  const handleMoveToOther = (phraseToMove: string) => {
    if (!withWritePermission) return;
    setPhrases(prev => prev.filter(p => p.toLowerCase() !== phraseToMove.toLowerCase()));
    setOtherPhrases(prev => {
      const lower = phraseToMove.toLowerCase();
      if (!prev.map(p => p.toLowerCase()).includes(lower)) {
        return [...prev, phraseToMove];
      }
      return prev;
    });
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const tableData = useMemo(() => {
    return phrases.map(phrase => {
      const existing = initialKeywords.find(k => k.content.toLowerCase() === phrase.toLowerCase());
      return {
        id: existing?.id ?? `new-${phrase}`,
        content: phrase,
        updatedDate: existing?.updatedDate ?? null,
        postKeyphrasesCount: existing?.postKeyphrasesCount ?? 0,
        isNew: !existing,
      };
    });
  }, [phrases, initialKeywords]);

  const filteredAndSortedData = useMemo(() => {
    const lowercaseQuery = searchQuery.toLowerCase().trim();
    const filtered = lowercaseQuery
      ? tableData.filter(item => item.content.toLowerCase().includes(lowercaseQuery))
      : tableData;

    return [...filtered].sort((a, b) => {
      let valA: any;
      let valB: any;

      if (sortColumn === "content") {
        valA = a.content.toLowerCase();
        valB = b.content.toLowerCase();
      } else if (sortColumn === "updatedDate") {
        valA = a.updatedDate ? new Date(a.updatedDate).getTime() : 0;
        valB = b.updatedDate ? new Date(b.updatedDate).getTime() : 0;
      } else {
        valA = a.postKeyphrasesCount;
        valB = b.postKeyphrasesCount;
      }

      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [tableData, searchQuery, sortColumn, sortDirection]);

  const updateKeywords = async () => {
    if (!withWritePermission) return;

    setLoading(true);
    try {
      if (movedCount > 0) {
        const otherPolicy = policy === "BLOCK" ? "FLAG" : "BLOCK";
        await KeyphraseServices.update({
          policy: otherPolicy,
          phrases: otherPhrases,
        });
      }

      await KeyphraseServices.update({
        policy,
        phrases,
      });
      toast.success("Keywords updated successfully.");
      invalidateKeywords();
      onUpdate();
    } catch (err) {
      console.error(err);
      setErrors(["Failed to save changes. Please try again."]);
    } finally {
      setLoading(false);
    }
  };

  const renderSortHeader = (column: SortColumn, headerLabel: string) => {
    const isSorted = sortColumn === column;
    return (
      <Table.Head
        onClick={() => handleSort(column)}
        className="font-semibold text-xs text-slate-500 uppercase tracking-wider py-2 cursor-pointer hover:text-slate-800 select-none transition-colors"
      >
        <div className="flex items-center gap-1">
          <span>{headerLabel}</span>
          {isSorted ? (
            sortDirection === "asc" ? (
              <ChevronUp className="size-3.5 text-slate-700" />
            ) : (
              <ChevronDown className="size-3.5 text-slate-700" />
            )
          ) : (
            <ArrowUpDown className="size-3 text-slate-400" />
          )}
        </div>
      </Table.Head>
    );
  };

  const renderSortHeaderRight = (column: SortColumn, headerLabel: string) => {
    const isSorted = sortColumn === column;
    return (
      <Table.Head
        onClick={() => handleSort(column)}
        className="font-semibold text-xs text-slate-500 uppercase tracking-wider py-2 cursor-pointer hover:text-slate-800 select-none transition-colors text-right"
      >
        <div className="flex items-center justify-end gap-1">
          <span>{headerLabel}</span>
          {isSorted ? (
            sortDirection === "asc" ? (
              <ChevronUp className="size-3.5 text-slate-700" />
            ) : (
              <ChevronDown className="size-3.5 text-slate-700" />
            )
          ) : (
            <ArrowUpDown className="size-3 text-slate-400" />
          )}
        </div>
      </Table.Head>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={isOpen => {
          if (!isOpen) onClose();
        }}
        size="3xl"
        className="h-[85vh] flex flex-col"
      >
        <Dialog.Header>
          <Dialog.Title className="flex items-center gap-2.5">
            <div
              className={`p-1.5 rounded-lg border flex items-center justify-center shrink-0 shadow-xs ${
                policy === "BLOCK"
                  ? "bg-red-50 text-red-600 border-red-200"
                  : "bg-amber-50 text-amber-600 border-amber-200"
              }`}
            >
              {policy === "BLOCK" ? <Ban className="size-4" /> : <Flag className="size-4" />}
            </div>
            <span>Edit {label}</span>
          </Dialog.Title>
          <Dialog.Description>
            Configure and manage keywords or short phrases for compliance filtering.
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Content className="flex-1 overflow-y-auto flex flex-col space-y-4 min-h-0">
          {errors && errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <Alert.Title>Error</Alert.Title>
              <Alert.Description>
                {errors.map((error, idx) => (
                  <p key={idx}>{error}</p>
                ))}
              </Alert.Description>
            </Alert>
          )}

          {isFetchingData ? (
            <div className="flex-1 flex flex-col items-center justify-center py-24 gap-3 text-muted-foreground font-sans">
              <Spinner className="size-8" />
              <span className="text-sm font-medium animate-pulse">Loading compliance keyword set...</span>
            </div>
          ) : (
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0">
              {/* Add Phrases Section (lg:col-span-4) */}
              <div className="flex flex-col space-y-3 lg:col-span-4 pr-2">
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold">Add New Phrases</h4>
                  <p className="text-xs text-muted-foreground leading-normal">
                    Type or paste phrases. Press{" "}
                    <kbd className="bg-slate-100 px-1.5 py-0.5 rounded border text-slate-600 font-mono text-[10px]">
                      Enter
                    </kbd>{" "}
                    to add, or paste spreadsheet rows/columns.
                  </p>
                </div>
                <Textarea
                  className="flex-1 min-h-[180px] resize-none text-sm font-sans"
                  placeholder="e.g. best interest rate, no closing costs"
                  value={pasteText}
                  onChange={e => setPasteText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={!withWritePermission}
                />
                <Button
                  onClick={handleAddPhrases}
                  disabled={!pasteText.trim() || !withWritePermission}
                  className="w-full flex items-center justify-center gap-1.5 animate-in fade-in duration-200"
                >
                  <Plus className="size-4" />
                  <span>Add to List</span>
                </Button>
              </div>

              {/* Current Phrases List Section (lg:col-span-8) */}
              <div className="flex flex-col space-y-3 lg:col-span-8 min-h-0">
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                      <span>Current Phrases</span>

                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full font-medium">
                          {phrases.length} total
                        </span>
                        {addedCount > 0 && (
                          <button
                            onClick={() => {
                              setChangesModalType("added");
                              setChangesModalOpen(true);
                            }}
                            className="text-xs inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-100 hover:bg-emerald-100 hover:border-emerald-200 cursor-pointer active:scale-95 transition-all font-bold"
                          >
                            +{addedCount} added
                          </button>
                        )}
                        {movedCount > 0 && (
                          <button
                            onClick={() => {
                              setChangesModalType("moved");
                              setChangesModalOpen(true);
                            }}
                            className="text-xs inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-100 hover:bg-blue-100 hover:border-blue-200 cursor-pointer active:scale-95 transition-all font-bold"
                          >
                            ⇄ {movedCount} moved
                          </button>
                        )}
                        {removedCount > 0 && (
                          <button
                            onClick={() => {
                              setChangesModalType("removed");
                              setChangesModalOpen(true);
                            }}
                            className="text-xs inline-flex items-center gap-1 bg-red-50 text-red-700 px-2 py-0.5 rounded-full border border-red-100 hover:bg-red-100 hover:border-red-200 cursor-pointer active:scale-95 transition-all font-bold"
                          >
                            -{removedCount} removed
                          </button>
                        )}
                      </div>
                    </h4>
                    {phrases.length > 0 && withWritePermission && (
                      <Button
                        variant="destructiveGhost"
                        size="xs"
                        onClick={handleRemoveAll}
                        className="text-xs h-6 px-2 text-destructive hover:bg-destructive/10 border-0 shadow-none font-bold"
                      >
                        <Trash className="size-3 h-3 mr-1" />
                        <span>Remove All</span>
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal">
                    Review, sort, filter, or delete active phrases and view their usage metrics.
                  </p>
                </div>

                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
                  <Input
                    className="pl-9 h-9 text-sm"
                    placeholder="Search phrases..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>

                <div className="flex-1 border border-border rounded-lg bg-slate-50/50 overflow-hidden flex flex-col min-h-0">
                  <div className="overflow-y-auto flex-1 min-h-0">
                    {filteredAndSortedData.length > 0 ? (
                      <table className="w-full caption-bottom text-sm">
                        <thead className="sticky top-0 bg-slate-100 z-10 shadow-xs border-b border-border">
                          <tr className="border-b border-border/80">
                            {renderSortHeader("content", "Phrase")}
                            {renderSortHeader("updatedDate", "Last Changed")}
                            {renderSortHeaderRight(
                              "postKeyphrasesCount",
                              policy === "BLOCK" ? "Times Blocked" : "Times Appeared"
                            )}
                            <th className="font-semibold text-xs text-slate-500 uppercase tracking-wider py-2 text-right w-20 px-4">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-border/60">
                          {filteredAndSortedData.map(item => (
                            <tr key={item.id} className="border-b border-border hover:bg-slate-50/60 transition-colors">
                              <td className="font-medium text-foreground py-2 px-4 break-all font-sans">
                                <div className="flex items-center gap-1.5">
                                  <span>{item.content}</span>
                                  {item.isNew && (
                                    <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded border border-emerald-100 uppercase tracking-wider shrink-0 select-none">
                                      New
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="text-muted-foreground py-2 px-4 text-xs font-sans">
                                {item.updatedDate ? (
                                  new Date(item.updatedDate).toLocaleDateString()
                                ) : (
                                  <span className="text-slate-400 italic">Unsaved</span>
                                )}
                              </td>
                              <td className="text-right font-semibold py-2 px-4 text-sm pr-10 font-sans">
                                {item.postKeyphrasesCount}
                              </td>
                              <td className="text-right py-2 px-4 shrink-0 pr-6">
                                <div className="flex items-center justify-end gap-1">
                                  <Tooltip>
                                    <Tooltip.Trigger>
                                      <button
                                        type="button"
                                        onClick={() => handleMoveToOther(item.content)}
                                        className="text-muted-foreground hover:text-foreground transition-opacity cursor-pointer p-1 rounded hover:bg-slate-200/60 focus:outline-none shrink-0"
                                      >
                                        {policy === "FLAG" ? (
                                          <Ban className="size-3.5 text-red-600" />
                                        ) : (
                                          <Flag className="size-3.5 text-amber-500" />
                                        )}
                                      </button>
                                    </Tooltip.Trigger>
                                    <Tooltip.Content>
                                      {policy === "FLAG" ? "Move to Blocked Phrases" : "Move to Flagged Phrases"}
                                    </Tooltip.Content>
                                  </Tooltip>
                                  {withWritePermission && (
                                    <Button
                                      variant="ghost"
                                      size="icon-xs"
                                      onClick={() => handleRemovePhrase(item.content)}
                                      className="text-destructive hover:bg-destructive/10 shrink-0"
                                      title={`Delete ${item.content}`}
                                    >
                                      <Trash2 className="size-3.5" />
                                      <span className="sr-only">Delete {item.content}</span>
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full py-12 text-center px-4">
                        <p className="text-sm text-muted-foreground font-sans">
                          {phrases.length === 0 ? "No phrases configured yet." : "No matching phrases found."}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Dialog.Content>

        <Dialog.Footer>
          {/* Right Side: Action Buttons */}
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={updateKeywords}
              loading={loading}
              disabled={!withWritePermission || (addedCount === 0 && removedCount === 0 && movedCount === 0)}
            >
              Save Changes
            </Button>
          </div>
        </Dialog.Footer>
      </Dialog>

      {/* Secondary Dialog to view added/removed/moved lists details */}
      <Dialog open={changesModalOpen} onOpenChange={setChangesModalOpen} size="md">
        <Dialog.Header>
          <Dialog.Title className="capitalize select-none text-sans font-bold">
            {changesModalType === "added"
              ? "Added Phrases"
              : changesModalType === "removed"
              ? "Removed Phrases"
              : `Moved to ${policy === "BLOCK" ? "Flagged" : "Blocked"}`}{" "}
            ({changesModalPhrases.length})
          </Dialog.Title>
          <Dialog.Description className="select-none text-sans">
            {changesModalType === "added"
              ? "These phrases have been newly added in this editing session."
              : changesModalType === "removed"
              ? "These phrases will be removed from your compliance configuration."
              : `These phrases will be moved to the ${
                  policy === "BLOCK" ? "Flagged Phrases" : "Blocked Phrases"
                } list.`}
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.Content className="py-2">
          <div className="border border-border rounded-lg bg-slate-50/50 max-h-[300px] overflow-y-auto divide-y divide-border/60">
            {changesModalPhrases.length > 0 ? (
              changesModalPhrases.map((phrase, idx) => (
                <div key={idx} className="py-2.5 px-3 text-sm text-foreground font-sans font-medium break-all">
                  {phrase}
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-sm text-muted-foreground font-sans italic select-none">
                No phrases to display.
              </div>
            )}
          </div>
        </Dialog.Content>
        <Dialog.Footer className="sm:justify-end">
          <Button variant="outline" onClick={() => setChangesModalOpen(false)}>
            Close
          </Button>
        </Dialog.Footer>
      </Dialog>
    </>
  );
};
